import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetPdpV1WorkspacesWorkspaceIdPdpsPdpIdGetData,
  type GetPdpV1WorkspacesWorkspaceIdPdpsPdpIdGetErrors,
  type GetPdpV1WorkspacesWorkspaceIdPdpsPdpIdGetResponse,
  getPdpV1WorkspacesWorkspaceIdPdpsPdpIdGet,
} from '../../generated';

export type GetPdpQueryVariables = GetPdpV1WorkspacesWorkspaceIdPdpsPdpIdGetData['path'] & {
  organization_id: string;
};

export type GetPdpQuery = GetPdpV1WorkspacesWorkspaceIdPdpsPdpIdGetResponse;

export const useGetPdpQuery = <
  TData = GetPdpQuery,
  TError = GetPdpV1WorkspacesWorkspaceIdPdpsPdpIdGetErrors[keyof GetPdpV1WorkspacesWorkspaceIdPdpsPdpIdGetErrors],
>(
  { organization_id, workspace_id, pdp_id }: GetPdpQueryVariables,
  options?: Omit<UseQueryOptions<GetPdpQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetPdpQuery, TError, TData>({
    queryKey: ['GetPdp', { organization_id, workspace_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getPdpV1WorkspacesWorkspaceIdPdpsPdpIdGet({
        path: { workspace_id, pdp_id },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetPdpQuery.getKey = ({ organization_id, workspace_id, pdp_id }: GetPdpQueryVariables) => [
  'GetPdp',
  { organization_id, workspace_id, pdp_id },
];

useGetPdpQuery.fetcher = ({ workspace_id, pdp_id }: GetPdpQueryVariables, _headers?: RequestInit['headers']) => {
  return async () => {
    const { data } = await getPdpV1WorkspacesWorkspaceIdPdpsPdpIdGet({
      path: { workspace_id, pdp_id },
      throwOnError: true,
    });
    return data;
  };
};

useGetPdpQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/pdps/{pdp_id}',
};
